import * as React from 'react';

import { useDevices } from 'utils/devices';
import { scrollElementIntoViewOnMobile } from 'utils/scrollElementIntoViewOnMobile';

type FocusableFieldStore = {
  focus: () => void;
};

export const useScrollFocusedFieldOnKeyboardResize = <T extends HTMLElement = HTMLInputElement>(
  store: FocusableFieldStore,
): {
  inputRef: React.RefObject<T>;
  handleFocus: (event: React.FocusEvent<T>) => void;
} => {
  const { isPhone } = useDevices();
  const inputRef = React.useRef<T>(null);

  const handleFocus = React.useCallback(
    (event: React.FocusEvent<T>) => {
      store.focus();
      scrollElementIntoViewOnMobile(event.currentTarget);
    },
    [store],
  );

  React.useEffect(() => {
    if (!isPhone) {
      return;
    }

    const viewport = window.visualViewport;

    if (!viewport) {
      return;
    }

    const handleViewportResize = (): void => {
      const input = inputRef.current;

      if (input && document.activeElement === input) {
        scrollElementIntoViewOnMobile(input);
      }
    };

    viewport.addEventListener('resize', handleViewportResize);

    return (): void => {
      viewport.removeEventListener('resize', handleViewportResize);
    };
  }, [isPhone]);

  return {
    inputRef,
    handleFocus,
  };
};
